import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

// Types
export type ClientInfoName =
  | 'goal'
  | 'profession'
  | 'general_insights'
  | 'conversion_potential'
  | 'conversion_reasoning';

export interface ConsultationClientInfo {
  id: number;
  consultationId: number;
  name: ClientInfoName;
  value: string | null;
  isAi: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export type MistakeCategory =
  | 'grammar'
  | 'calque'
  | 'vocabulary'
  | 'code_switching'
  | 'pronunciation'
  | 'other';

export interface ConsultationTask {
  id: number;
  consultationId: number;
  description: string;
  done: boolean;
  isAi: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ConsultationMistake {
  id: number;
  consultationId: number;
  category: MistakeCategory;
  quote: string | null;
  quoteStartMs: number | null;
  quoteEndMs: number | null;
  explanation: string;
  isAi: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface ConsultationAiInsightsJob {
  id: number;
  consultationId: number;
  recordingId: number | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  attempts: number;
  lastError: string | null;
  generalModel: string | null;
  assessmentModel: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  costUsd: string | number | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt?: string;
  updatedAt?: string;
}

// ---- Selling review (docs/specs/consultation-selling-review.md) ----------

export type SellingFit = 'fit' | 'partial' | 'no_group' | 'unclear';
export type SellingDecisionOutcome = 'joining' | 'declined' | 'waiting_for_group' | 'thinking_with_plan' | 'unclear';
export type SellingDiscoveryState = 'covered' | 'partial' | 'missing' | 'not_needed';
export type SellingFindingKind = 'mistake' | 'strength' | 'concern' | 'want' | 'constraint';
export type SellingFindingSeverity = 'major' | 'minor';
export type SellingConcernHandling = 'handled' | 'badly_answered' | 'unanswered';

export interface ConsultationSellingReviewJob {
  id: number;
  consultationId: number;
  recordingId: number | null;
  transcriptionJobId: number | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  attempts: number;
  lastError: string | null;
  promptVersion: string | null;
  analysisModel: string | null;
  adviceModel: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  costUsd: string | number | null;
  droppedItems: number | null;
  startedAt: string | null;
  completedAt: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface ConsultationSellingFinding {
  id: number;
  kind: SellingFindingKind;
  code: string;
  severity: SellingFindingSeverity | null;
  handling: SellingConcernHandling | null;
  quote: string;
  quoteSpeaker: 'Teacher' | 'Client';
  startMs: number | null;
  contextQuote: string | null;
  contextStartMs: number | null;
  // Mistakes only: what the mistake changed in this call. Null on older reviews.
  consequence: string | null;
  explanation: string;
}

export interface ConsultationSellingAdviceFinding {
  kind: SellingFindingKind;
  code: string;
  quote: string;
  startMs: number | null;
}

export interface ConsultationSellingAdviceCard {
  habit: string;
  title: string;
  whatHappened: string;
  why: string;
  tryNextTime: string[];
  findings: ConsultationSellingAdviceFinding[];
}

export interface ConsultationSellingWentWell {
  text: string;
  code: string;
  quote: string;
  startMs: number | null;
}

export interface ConsultationSellingDiscovery {
  goal: SellingDiscoveryState;
  pastExperience: SellingDiscoveryState;
  constraints: SellingDiscoveryState;
  decisionCriteria: SellingDiscoveryState;
}

export interface ConsultationSellingReview {
  id: number;
  jobId: number;
  transcriptionJobId: number | null;
  promptVersion: string | null;
  completedAt: string | null;
  summary: string | null;
  fit: SellingFit;
  fitReason: string | null;
  decisionOutcome: SellingDecisionOutcome;
  decisionDetail: string | null;
  decisionStartMs: number | null;
  discovery: ConsultationSellingDiscovery;
  headline: string | null;
  wentWell: ConsultationSellingWentWell | null;
  advice: ConsultationSellingAdviceCard[];
  findings: {
    mistakes: ConsultationSellingFinding[];
    strengths: ConsultationSellingFinding[];
    concerns: ConsultationSellingFinding[];
    wants: ConsultationSellingFinding[];
    constraints: ConsultationSellingFinding[];
  };
}

export interface ConsultationRecordingSummary {
  id: number;
  consultationId: number;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'uploading';
  channelTeacher: 1 | 2;
  transcriptionMode: 'multichannel' | 'mixed';
  activeJobId: number | null;
  activeJob?: { id: number; status: string; completedAt: string | null } | null;
  sampleQuotes?: { channel1: string | null; channel2: string | null };
  healthy?: boolean;
  healthReason?: string | null;
  healthCheckedAt?: string | null;
}

export interface Consultation {
  id: number;
  adId?: number | null;
  isOrganic: boolean;
  firstName?: string | null;
  lastName?: string | null;
  patronymic?: string | null;
  contact?: string | null;
  firstContactDate?: string | null;
  consultationDate: string;
  consultationTime?: string | null;
  status?: 'scheduled' | 'cancelled' | 'awaiting_payment' | 'waitlist' | 'paid' | 'not_interested' | null;
  langLevel?: string | null;
  studentId?: number | null;
  suggestedGroupId?: number | null;
  contactId?: number | null;
  referrerStudentId?: number | null;
  commentary?: string | null;
  lostReason?: string | null;
  lostReasonOther?: string | null;
  ad?: {
    id: number;
    name: string;
  };
  student?: {
    id: number;
    firstName?: string | null;
    lastName?: string | null;
  } | null;
  referrer?: {
    id: number;
    firstName?: string | null;
    lastName?: string | null;
    surname?: string | null;
    quitDate?: string | null;
  } | null;
  suggestedGroup?: {
    id: number;
    name: string | null;
    level: string;
  } | null;
  recordingCount?: number;
  recordingId?: number | null;
  recordingStatus?: 'pending' | 'processing' | 'completed' | 'failed' | null;
  recordingHealthy?: boolean | null;
  pendingReferralReward?: boolean;
  // AI insights side-data — only populated on GET /:id, not on the list endpoint.
  clientInfo?: ConsultationClientInfo[];
  tasks?: ConsultationTask[];
  mistakes?: ConsultationMistake[];
  aiInsightsJob?: ConsultationAiInsightsJob | null;
  sellingReviewJob?: ConsultationSellingReviewJob | null;
  sellingReview?: ConsultationSellingReview | null;
  recording?: ConsultationRecordingSummary | null;
  statusHistory?: ConsultationStatusHistoryEntry[];
  createdAt?: string;
  updatedAt?: string;
}

export interface ConsultationStatusHistoryEntry {
  id: number;
  fromStatus: 'scheduled' | 'cancelled' | 'awaiting_payment' | 'waitlist' | 'paid' | 'not_interested' | null;
  toStatus: 'scheduled' | 'cancelled' | 'awaiting_payment' | 'waitlist' | 'paid' | 'not_interested';
  changedByName: string | null;
  createdAt: string;
}

export interface ConsultationsState {
  consultations: Consultation[];
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
  filters: {
    search: string;
    adId: string;
    status: string;
    langLevel: string;
    dateFrom: string;
    dateTo: string;
    hasRecording: string;
  };
}

// Initial state
const initialState: ConsultationsState = {
  consultations: [],
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 50,
  },
  filters: {
    search: '',
    adId: 'all',
    status: 'all',
    langLevel: 'all',
    dateFrom: '',
    dateTo: '',
    hasRecording: 'all',
  },
};

// API Base URL
const API_BASE_URL = import.meta.env.VITE_API_URL as string;

// Helper function to get auth headers
const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Async thunks
export const fetchConsultationsAsync = createAsyncThunk(
  'consultations/fetchConsultations',
  async (params: { 
    page?: number; 
    limit?: number; 
    search?: string;
    adId?: string;
    status?: string;
    langLevel?: string;
    dateFrom?: string;
    dateTo?: string;
    hasRecording?: string;
  } = {}, { rejectWithValue, getState }) => {
    try {
      const { page = 1, limit = 50, search = '', adId = 'all', status = 'all', langLevel = 'all', dateFrom = '', dateTo = '', hasRecording = 'all' } = params;
      
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      
      if (search) {
        queryParams.append('search', search);
      }
      if (adId && adId !== 'all') {
        queryParams.append('adId', adId);
      }
      if (status && status !== 'all') {
        queryParams.append('status', status);
      }
      if (langLevel && langLevel !== 'all') {
        queryParams.append('langLevel', langLevel);
      }
      if (dateFrom) {
        queryParams.append('dateFrom', dateFrom);
      }
      if (dateTo) {
        queryParams.append('dateTo', dateTo);
      }
      if (hasRecording && hasRecording !== 'all') {
        queryParams.append('hasRecording', hasRecording);
      }
      const response = await axios.get(
        `${API_BASE_URL}/consultations?${queryParams.toString()}`,
        { headers: getAuthHeaders(getState) }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch consultations'));
    }
  }
);

export const createConsultationAsync = createAsyncThunk(
  'consultations/createConsultation',
  async (consultationData: Partial<Consultation>, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/consultations`,
        consultationData,
        { headers: getAuthHeaders(getState) }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create consultation'));
    }
  }
);

export const updateConsultationAsync = createAsyncThunk(
  'consultations/updateConsultation',
  async ({ id, data }: { id: number; data: Partial<Consultation> }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/consultations/${id}`,
        data,
        { headers: getAuthHeaders(getState) }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update consultation'));
    }
  }
);

export const updateConsultationStatusAsync = createAsyncThunk(
  'consultations/updateStatus',
  async ({ id, status, lostReason, lostReasonOther }: { id: number; status: 'scheduled' | 'cancelled' | 'awaiting_payment' | 'waitlist' | 'paid' | 'not_interested'; lostReason?: string | null; lostReasonOther?: string | null }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/consultations/${id}/status`,
        { status, lostReason, lostReasonOther },
        { headers: getAuthHeaders(getState) }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update status'));
    }
  }
);

export const updateConsultationLevelAsync = createAsyncThunk(
  'consultations/updateLevel',
  async ({ id, langLevel }: { id: number; langLevel: string | null }, { rejectWithValue, getState }) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/consultations/${id}/level`,
        { langLevel },
        { headers: getAuthHeaders(getState) }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update language level'));
    }
  }
);

export const deleteConsultationAsync = createAsyncThunk(
  'consultations/deleteConsultation',
  async (id: number, { rejectWithValue, getState }) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/consultations/${id}`,
        { headers: getAuthHeaders(getState) }
      );

      return id;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete consultation'));
    }
  }
);

// ---- AI Insights thunks ----------------------------------------------------
// These don't update the list slice — they're used directly from the detail
// page component, which holds the detail payload in local state.

export const generateAiInsightsAsync = createAsyncThunk(
  'consultations/generateAiInsights',
  async (consultationId: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/consultations/${consultationId}/ai-insights`,
        {},
        { headers: getAuthHeaders(getState) }
      );
      return response.data.job as ConsultationAiInsightsJob;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to enqueue AI insights job'));
    }
  }
);

export const fetchAiInsightsJobAsync = createAsyncThunk(
  'consultations/fetchAiInsightsJob',
  async (consultationId: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/consultations/${consultationId}/ai-insights/jobs/latest`,
        { headers: getAuthHeaders(getState) }
      );
      return response.data.job as ConsultationAiInsightsJob | null;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch AI insights job'));
    }
  }
);

// ---- Selling review thunks --------------------------------------------
// Mirror the AI insights thunks above; also don't touch the list slice.

export const rerunSellingReviewAsync = createAsyncThunk(
  'consultations/rerunSellingReview',
  async (consultationId: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/consultations/${consultationId}/selling-review`,
        {},
        { headers: getAuthHeaders(getState) }
      );
      return response.data.job as ConsultationSellingReviewJob;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to enqueue selling review job'));
    }
  }
);

export const fetchSellingReviewJobAsync = createAsyncThunk(
  'consultations/fetchSellingReviewJob',
  async (consultationId: number, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/consultations/${consultationId}/selling-review/jobs/latest`,
        { headers: getAuthHeaders(getState) }
      );
      return response.data.job as ConsultationSellingReviewJob | null;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch selling review job'));
    }
  }
);

export const clearAiInsightsAsync = createAsyncThunk(
  'consultations/clearAiInsights',
  async (consultationId: number, { rejectWithValue, getState }) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/consultations/${consultationId}/ai-insights`,
        { headers: getAuthHeaders(getState) }
      );
      return consultationId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to clear AI insights'));
    }
  }
);

export const upsertClientInfoAsync = createAsyncThunk(
  'consultations/upsertClientInfo',
  async (
    { consultationId, name, value }: { consultationId: number; name: ClientInfoName; value: string },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/consultations/${consultationId}/client-info/${name}`,
        { value },
        { headers: getAuthHeaders(getState) }
      );
      return response.data.clientInfo as ConsultationClientInfo;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to save field'));
    }
  }
);

export const deleteClientInfoAsync = createAsyncThunk(
  'consultations/deleteClientInfo',
  async (
    { consultationId, name }: { consultationId: number; name: ClientInfoName },
    { rejectWithValue, getState }
  ) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/consultations/${consultationId}/client-info/${name}`,
        { headers: getAuthHeaders(getState) }
      );
      return { consultationId, name };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to clear field'));
    }
  }
);

export const createTaskAsync = createAsyncThunk(
  'consultations/createTask',
  async (
    { consultationId, description }: { consultationId: number; description: string },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/consultations/${consultationId}/tasks`,
        { description },
        { headers: getAuthHeaders(getState) }
      );
      return response.data.task as ConsultationTask;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create task'));
    }
  }
);

export const updateTaskAsync = createAsyncThunk(
  'consultations/updateTask',
  async (
    { consultationId, taskId, patch }: { consultationId: number; taskId: number; patch: Partial<Pick<ConsultationTask, 'description' | 'done'>> },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/consultations/${consultationId}/tasks/${taskId}`,
        patch,
        { headers: getAuthHeaders(getState) }
      );
      return response.data.task as ConsultationTask;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update task'));
    }
  }
);

export const deleteTaskAsync = createAsyncThunk(
  'consultations/deleteTask',
  async (
    { consultationId, taskId }: { consultationId: number; taskId: number },
    { rejectWithValue, getState }
  ) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/consultations/${consultationId}/tasks/${taskId}`,
        { headers: getAuthHeaders(getState) }
      );
      return taskId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete task'));
    }
  }
);

export const createMistakeAsync = createAsyncThunk(
  'consultations/createMistake',
  async (
    { consultationId, mistake }: { consultationId: number; mistake: Omit<ConsultationMistake, 'id' | 'consultationId' | 'isAi' | 'createdAt' | 'updatedAt'> },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/consultations/${consultationId}/mistakes`,
        mistake,
        { headers: getAuthHeaders(getState) }
      );
      return response.data.mistake as ConsultationMistake;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create mistake'));
    }
  }
);

export const updateMistakeAsync = createAsyncThunk(
  'consultations/updateMistake',
  async (
    { consultationId, mistakeId, patch }: { consultationId: number; mistakeId: number; patch: Partial<Omit<ConsultationMistake, 'id' | 'consultationId' | 'createdAt' | 'updatedAt'>> },
    { rejectWithValue, getState }
  ) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/consultations/${consultationId}/mistakes/${mistakeId}`,
        patch,
        { headers: getAuthHeaders(getState) }
      );
      return response.data.mistake as ConsultationMistake;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update mistake'));
    }
  }
);

export const deleteMistakeAsync = createAsyncThunk(
  'consultations/deleteMistake',
  async (
    { consultationId, mistakeId }: { consultationId: number; mistakeId: number },
    { rejectWithValue, getState }
  ) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/consultations/${consultationId}/mistakes/${mistakeId}`,
        { headers: getAuthHeaders(getState) }
      );
      return mistakeId;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete mistake'));
    }
  }
);

// Slice
const consultationsSlice = createSlice({
  name: 'consultations',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setFilters: (state, action: PayloadAction<{
      search?: string;
      adId?: string;
      status?: string;
      langLevel?: string;
      dateFrom?: string;
      dateTo?: string;
      hasRecording?: string;
    }>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = {
        search: '',
        adId: 'all',
        status: 'all',
        langLevel: 'all',
        dateFrom: '',
        dateTo: '',
        hasRecording: 'all',
      };
      state.pagination.currentPage = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch consultations
      .addCase(fetchConsultationsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConsultationsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.consultations = action.payload.consultations;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchConsultationsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create consultation
      .addCase(createConsultationAsync.fulfilled, (state, action) => {
        state.consultations.unshift(action.payload.consultation);
      })
      // Update consultation
      .addCase(updateConsultationAsync.fulfilled, (state, action) => {
        const index = state.consultations.findIndex(c => c.id === action.payload.consultation.id);
        if (index !== -1) {
          state.consultations[index] = action.payload.consultation;
        }
      })
      // Update status
      .addCase(updateConsultationStatusAsync.fulfilled, (state, action) => {
        const index = state.consultations.findIndex(c => c.id === action.payload.consultation.id);
        if (index !== -1) {
          state.consultations[index] = action.payload.consultation;
        }
      })
      // Update level
      .addCase(updateConsultationLevelAsync.fulfilled, (state, action) => {
        const index = state.consultations.findIndex(c => c.id === action.payload.consultation.id);
        if (index !== -1) {
          state.consultations[index] = action.payload.consultation;
        }
      })
      // Delete consultation
      .addCase(deleteConsultationAsync.fulfilled, (state, action) => {
        state.consultations = state.consultations.filter(c => c.id !== action.payload);
      });
  },
});

export const { clearError, setFilters, clearFilters, setPage } = consultationsSlice.actions;
export default consultationsSlice.reducer;
